import { useMemo, useState } from "react";
import { Slider } from "./ui/slider";
import {
  applyAdjustments,
  hexToRgb,
  rgbToHex,
  rgbToCmyk,
  simulatePrint,
  formatRgb,
  formatCmyk,
  textOn,
  printFidelity,
} from "../lib/color";
import { CopyButton } from "./CopyButton";
import { RotateCcw } from "lucide-react";

const DEFAULTS = {
  tint: 0,
  shade: 0,
  tone: 0,
  hueShift: 0,
  satDelta: 0,
  brightDelta: 0,
};

const SLIDERS = [
  { key: "tint",        label: "Tint",       min: 0,    max: 100, unit: "%", testId: "slider-tint",        bipolar: false },
  { key: "shade",       label: "Shade",      min: 0,    max: 100, unit: "%", testId: "slider-shade",       bipolar: false },
  { key: "tone",        label: "Tone",       min: 0,    max: 100, unit: "%", testId: "slider-tone",        bipolar: false },
  { key: "satDelta",    label: "Saturation", min: -100, max: 100, unit: "%", testId: "slider-saturation",  bipolar: true  },
  { key: "hueShift",    label: "Hue",        min: -180, max: 180, unit: "°", testId: "slider-hue",         bipolar: true  },
  { key: "brightDelta", label: "Brightness", min: -100, max: 100, unit: "%", testId: "slider-brightness",  bipolar: true  },
];

function PreviewPanel({ testId, bg, txt, topLabel, topRight, rows, borderLeft, copyValue, copyTestId }) {
  return (
    <div
      data-testid={testId}
      className={`relative p-4 flex flex-col justify-between min-h-[260px] transition-colors duration-150 ${
        borderLeft ? "border-l border-border" : ""
      }`}
      style={{ backgroundColor: bg }}
    >
      <div className="flex items-start justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: txt }}>
          {topLabel}
        </span>
        {copyValue ? (
          <CopyButton value={copyValue} testId={copyTestId} textColor={txt} />
        ) : (
          <span className="font-mono text-[10px]" style={{ color: txt }}>
            {topRight}
          </span>
        )}
      </div>
      <div className="space-y-1.5" style={{ color: txt }}>
        {rows.map((r) => (
          <div key={r} className="font-mono text-xs">{r}</div>
        ))}
      </div>
    </div>
  );
}

function useAdjustedColor(hex, adj) {
  const baseRgb = useMemo(() => hexToRgb(hex), [hex]);
  const adjustedRgb = useMemo(() => applyAdjustments(baseRgb, adj), [baseRgb, adj]);
  return useMemo(() => {
    const adjustedHex = rgbToHex(adjustedRgb);
    const cmyk = rgbToCmyk(adjustedRgb);
    const printRgb = simulatePrint(adjustedRgb);
    const printHex = rgbToHex(printRgb);
    return {
      adjustedRgb,
      adjustedHex,
      cmyk,
      printRgb,
      printHex,
      txt: textOn(adjustedRgb),
      txtPrint: textOn(printRgb),
      fidelity: printFidelity(adjustedRgb),
    };
  }, [adjustedRgb]);
}

function AdjustedPanels({ data }) {
  const { adjustedRgb, adjustedHex, cmyk, printHex, txt, txtPrint, fidelity } = data;
  return (
    <div className="grid grid-cols-2 border-r border-border lg:col-span-2">
      <PreviewPanel
        testId="adjusted-preview-screen"
        bg={adjustedHex}
        txt={txt}
        topLabel="Screen"
        copyValue={adjustedHex}
        copyTestId="copy-hex-adjusted"
        rows={[
          `HEX ${adjustedHex.toUpperCase()}`,
          `RGB ${formatRgb(adjustedRgb)}`,
        ]}
      />
      <PreviewPanel
        testId="adjusted-preview-print"
        bg={printHex}
        txt={txtPrint}
        topLabel="Print sim"
        topRight={`Fid ${fidelity}`}
        borderLeft
        rows={[
          `CMYK ${formatCmyk(cmyk)}`,
          `HEX·SIM ${printHex.toUpperCase()}`,
        ]}
      />
    </div>
  );
}

function SliderRow({ slider, value, onChange, onReset }) {
  const { key, label, min, max, unit, testId, bipolar } = slider;
  const prefix = bipolar && value > 0 ? "+" : "";
  const isDefault = value === 0;
  return (
    <div className="grid grid-cols-[110px_1fr_60px_24px] items-center gap-4">
      <span className="text-[10px] uppercase tracking-[0.2em] text-muted">{label}</span>
      <Slider
        data-testid={testId}
        value={[value]}
        min={min}
        max={max}
        step={1}
        onValueChange={(v) => onChange(key, v[0])}
      />
      <span className="font-mono text-xs text-right">
        {prefix}{value}{unit}
      </span>
      <button
        type="button"
        onClick={() => onReset(key)}
        disabled={isDefault}
        data-testid={`${testId}-reset`}
        aria-label={`Reset ${label}`}
        title={`Reset ${label}`}
        className="h-6 w-6 inline-flex items-center justify-center text-muted hover:text-foreground disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
      >
        <RotateCcw className="h-3 w-3" strokeWidth={1.5} />
      </button>
    </div>
  );
}

export function ColorAdjuster({ hex, onCommit }) {
  const [adj, setAdj] = useState(DEFAULTS);
  const onSliderChange = (key, val) => setAdj((p) => ({ ...p, [key]: val }));
  const onSliderReset = (key) => setAdj((p) => ({ ...p, [key]: DEFAULTS[key] }));

  const data = useAdjustedColor(hex, adj);

  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between border-b border-border pb-2">
        <h2 className="text-sm font-bold uppercase tracking-[0.2em]">Adjust Active Colour</h2>
        <button
          onClick={() => setAdj(DEFAULTS)}
          data-testid="adjuster-reset"
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted hover:text-foreground transition-colors"
        >
          <RotateCcw className="h-3 w-3" strokeWidth={1.5} />
          Reset
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border border-border">
        <AdjustedPanels data={data} />
        <div className="lg:col-span-3 p-5 space-y-4 bg-background">
          {SLIDERS.map((s) => (
            <SliderRow
              key={s.key}
              slider={s}
              value={adj[s.key]}
              onChange={onSliderChange}
              onReset={onSliderReset}
            />
          ))}
          <div className="pt-3 flex items-center justify-end gap-3 border-t border-border">
            <button
              onClick={() => onCommit(data.adjustedHex)}
              data-testid="adjuster-set-active"
              className="h-9 px-4 bg-foreground text-background text-xs font-medium uppercase tracking-[0.2em] hover:opacity-90 transition-opacity"
            >
              Set as active
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
